/**
 * Сервис для работы с IndexedDB для хранения истории снимков
 */

import { Analysis } from '../types';

interface Snapshot {
  id: string;
  timestamp: number;
  analysis: Analysis;
  description: string;
}

interface SnapshotHistory {
  currentResultId: string;
  snapshots: Snapshot[];
  currentIndex: number;
}

const DB_NAME = 'plinus_snapshot_history';
const DB_VERSION = 1;
const STORE_NAME = 'histories';

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Открывает базу данных IndexedDB
 */
function openDB(): Promise<IDBDatabase> {
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Не удалось открыть IndexedDB'));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Создаем хранилище, если его нет
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'resultId' });
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });

  return dbPromise;
}

/**
 * Получает историю снимков для конкретного результата
 */
export async function getSnapshotHistory(resultId: string): Promise<SnapshotHistory | null> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(resultId);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const data = request.result;
        if (data) {
          resolve(data.history);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Ошибка при получении истории из IndexedDB:', error);
    return null;
  }
}

/**
 * Сохраняет историю снимков для конкретного результата
 */
export async function saveSnapshotHistory(resultId: string, history: SnapshotHistory): Promise<boolean> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const data = {
      resultId,
      history,
      timestamp: Date.now()
    };

    const request = store.put(data);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(true);
      request.onerror = () => {
        console.error('Ошибка при сохранении истории в IndexedDB:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('Ошибка при сохранении истории в IndexedDB:', error);
    return false;
  }
}

/**
 * Удаляет историю снимков для конкретного результата
 */
export async function deleteSnapshotHistory(resultId: string): Promise<boolean> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(resultId);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Ошибка при удалении истории из IndexedDB:', error);
    return false;
  }
}

/**
 * Получает список всех resultId с сохраненными историями
 */
export async function getAllResultIds(): Promise<string[]> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAllKeys();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        resolve(request.result as string[]);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Ошибка при получении списка результатов из IndexedDB:', error);
    return [];
  }
}

/**
 * Получает время последнего доступа к истории результата
 */
export async function getResultHistoryTimestamp(resultId: string): Promise<number> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(resultId);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const data = request.result;
        resolve(data?.timestamp || 0);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Ошибка при получении timestamp из IndexedDB:', error);
    return 0;
  }
}

/**
 * Очищает старые истории для освобождения места
 */
export async function clearOldHistories(keepCount: number = 1): Promise<number> {
  try {
    const allResultIds = await getAllResultIds();
    
    if (allResultIds.length <= keepCount) {
      return 0;
    }

    // Получаем timestamp для каждого результата
    const resultsWithTimestamp = await Promise.all(
      allResultIds.map(async (resultId) => ({
        resultId,
        timestamp: await getResultHistoryTimestamp(resultId)
      }))
    );

    // Сортируем по timestamp (от старых к новым)
    resultsWithTimestamp.sort((a, b) => a.timestamp - b.timestamp);

    // Удаляем самые старые (кроме последних keepCount)
    const toDelete = resultsWithTimestamp.slice(0, -keepCount);
    
    let deletedCount = 0;
    for (const { resultId } of toDelete) {
      const success = await deleteSnapshotHistory(resultId);
      if (success) {
        deletedCount++;
      }
    }

    return deletedCount;
  } catch (error) {
    console.error('Ошибка при очистке старых историй:', error);
    return 0;
  }
}

/**
 * Безопасное сохранение с автоматической очисткой при переполнении
 */
export async function safeSaveSnapshotHistory(resultId: string, history: SnapshotHistory): Promise<boolean> {
  try {
    return await saveSnapshotHistory(resultId, history);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.warn('⚠️ IndexedDB переполнен, очищаем старые истории...');
      
      // Очищаем старые истории, оставляя только последнюю
      const deletedCount = await clearOldHistories(1);
      
      if (deletedCount > 0) {
        console.log(`🧹 Очищено ${deletedCount} старых историй`);
        
        // Пытаемся сохранить снова
        try {
          return await saveSnapshotHistory(resultId, history);
        } catch (retryError) {
          console.error('❌ Не удалось сохранить данные даже после очистки:', retryError);
          return false;
        }
      }
    }
    
    console.error('❌ Ошибка при сохранении в IndexedDB:', error);
    return false;
  }
}

